import * as React from 'react';
import { Dispatch } from 'redux';
import { connect, useSelector } from 'react-redux';
import SimpleArtist from '@common/types/artist/simple-artist';
import styled from 'styled-components';
import { ContactInfo } from '@webapp/types/common/contact-info';
import { CreateInvites } from '@webapp/store/invites/action-creators';
import { NewInviteDraft } from '@webapp/store/invites/action-creators';
import { EditInviteDraft } from '@webapp/store/invites/action-creators';
import AppState from '@webapp/store/state';
import { Translate } from 'react-localize-redux';
import { DEFAULT_COUNTRY } from '../../form/email-or-phone/index';
import { Country } from '@common/types/data/country';
import EmailOrPhoneCtrl from '@webapp/controllers/form/email-or-phone';
import { UserSearchResult } from '../../../types/user-search-result';

import { TracInviteTypes } from '@services/invites/types/invite-types';
import Flex from '@components/layout/flex';
import Button from '@components/actions/button';
import { NewTracInvite } from '@services/invites/types/trac-invite';
import SmallNotification from '@components/small-notification';

interface InviteUserInlineCtrlProps {
  className?: string;
  name: string;
  role: string;
  disableInvite?: boolean;
  type: TracInviteTypes;
  metadata?: Record<string, any>;
  onCancel: () => void;
  onInvite: (ownsership: UserSearchResult) => void;
  sendInvites: (type: TracInviteTypes) => CreateInvites;
  createDraftInvite: (
    type: TracInviteTypes,
    inviter: SimpleArtist
  ) => NewInviteDraft;
  editDraftInvite: (
    type: TracInviteTypes,
    key: string,
    invite: Partial<NewTracInvite>
  ) => EditInviteDraft;
  setContact?: React.Dispatch<React.SetStateAction<ContactInfo | undefined>>;
}

interface MappedProps {
  activeArtist: SimpleArtist;
}

const mapStateToProps = (state: AppState) => ({
  activeArtist: state.artist.active,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  sendInvites: (type: TracInviteTypes) => dispatch(new CreateInvites(type)),
  createDraftInvite: (type: TracInviteTypes, inviter: SimpleArtist) =>
    dispatch(new NewInviteDraft(type, inviter)),
  editDraftInvite: (
    type: TracInviteTypes,
    key: string,
    invite: Partial<NewTracInvite>
  ) => dispatch(new EditInviteDraft(type, key, invite)),
});

export const InviteUserInlineCtrl: React.FC<
  InviteUserInlineCtrlProps & MappedProps
> = (props) => {
  const [contact, setContact] = React.useState<ContactInfo>();
  const [country, setCountry] = React.useState<Country>(DEFAULT_COUNTRY);
  const [showError, setShowError] = React.useState(false);
  const [showNotification, setShowNotification] = React.useState(false);

  React.useEffect(() => {
    setContact && setContact(contact);
  }, [contact, setContact]);

  React.useEffect(() => {
    if (showNotification) {
      const timer = setTimeout(() => {
        setShowNotification(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showNotification]);

  const currentInvites = useSelector((state: AppState) =>
    Object.keys(state.invites.current[props.type]).map(
      (i) => state.invites.current[props.type][i]
    )
  );

  const handleInvite = () => {
    if (!contact) {
      return;
    }
    setShowError(false);
    if (!props.role) {
      setShowError(true);
      return;
    }
    if (currentInvites.find((i) => i.email_or_phone === contact.value)?.id) {
      setShowNotification(true);
      return;
    }
    props.createDraftInvite(props.type, props.activeArtist);

    [
      {
        email_or_phone: contact.value,
        contact_type: contact.type,
        name: props.name,
        role: props.role.toLowerCase(),
        type: props.type,
        metadata: {
          artist: props.activeArtist,
          inviter: props.activeArtist,
          ...props.metadata,
        },
      },
    ].map((invite) =>
      props.editDraftInvite(props.type, invite.email_or_phone, invite)
    );
    props.sendInvites(props.type);

    props.onInvite({
      id:
        currentInvites.find((i) => i.email_or_phone === contact.value)?.id ||
        '',
      type: 'invite',
      name: props.name,
      contact: contact.value,
      helpText: '',
    });
  };

  return (
    <>
      <EmailOrPhoneCtrl
        className={props.className}
        value={contact?.value}
        country={country}
        onChange={(newContact) => setContact(newContact)}
        onCountryChange={(c) => setCountry(c)}
        error={showError ? <Translate id="INVITE.MISSING_ROLE" /> : null}
      />
      <Flex gap={5} className="mt-10">
        <Button buttonType="secondary" onClick={props.onCancel} fullWidth>
          <Translate id="ARTIST_PROFILE.TEAM.TEAM.CANCEL.LABEL" />
        </Button>
        <Button
          disabled={!contact?.value}
          buttonType="secondary-dark"
          onClick={handleInvite}
          fullWidth
        >
          <Translate id="ARTIST_PROFILE.TEAM.TEAM.SEND.LABEL" />
        </Button>
      </Flex>
      {showNotification ? (
        <SmallNotification
          notificationType="error"
          title={<Translate id="INVITE.LABEL.INVITE_EXISTS.SNACKBAR" />}
        />
      ) : null}
    </>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(InviteUserInlineCtrl)`
  .inline-button {
    width: auto;
    padding: 8px 14px;
    min-height: 32px !important;
    min-width: 68px;
  }
`);
